import { EmptyNotificationList } from "@/pages/notification-list/ui/empty-notification-list";
import { NotificationList } from "@/pages/notification-list/ui/notification-list";
import { NotificationPagination } from "@/pages/notification-list/ui/notification-list/notification-pagination";
import { PaginationControls } from "@/pages/notification-list/ui/notification-list/pagination-controls";
import type { Loadable } from "@/shared/lib/loadable";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseCheckbox } from "@/shared/ui/base-checkbox";
import { CircularProgress, Stack, Typography } from "@mui/material";
import type { FreightMatching } from "@x-mile/api-contract";

export type NotificationListPageProps = {
  page: number;
  perPage: number;
  loadableNotifications: Loadable<
    Pick<FreightMatching.GetNotificationsQueryResult, "data">
  >;
  loadableOfferCargos: Record<
    number,
    Loadable<Pick<FreightMatching.GetOffersCargoQueryResult, "data">>
  >;
  unreadOnly?: boolean;
  setUnreadOnly: (unreadOnly: boolean) => void;
  readNotification: (id: number) => Promise<void>;
  readAllNotifications: (ids: number[]) => Promise<void>;
};

export const NotificationListPageUi = ({
  page,
  perPage,
  loadableNotifications,
  loadableOfferCargos,
  unreadOnly,
  setUnreadOnly,
  readNotification,
  readAllNotifications,
}: NotificationListPageProps) => {
  const totalCount = loadableNotifications.data?.data.totalCount ?? 0;
  const count = Math.ceil(totalCount / perPage);

  if (loadableNotifications.status === "loading") {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (loadableNotifications.status === "error") {
    return <div>エラーが発生しました</div>;
  }

  const { notifications } = loadableNotifications.data.data;

  return (
    <Stack px={{ xs: 2, sm: 4 }} pb={{ xs: 2, sm: 4 }} spacing={4}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          お知らせ
        </Typography>
      </Stack>

      {notifications.length === 0 ? (
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={{ xs: 2, sm: 0 }}
          >
            {/* 既読・未読 */}
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              justifyContent="start"
            >
              <BaseButton
                variant="outlined"
                size="large"
                sx={{ minWidth: "initial" }}
                onClick={() =>
                  readAllNotifications(
                    notifications
                      .filter((notification) => !notification.isRead)
                      .map((notification) => notification.id),
                  )
                }
              >
                すべて既読にする
              </BaseButton>

              {/* biome-ignore lint/a11y/noLabelWithoutControl: 中身がコントロールなので無視してOK */}
              <label>
                <BaseCheckbox
                  label="未読のみ表示"
                  size="small"
                  value={unreadOnly}
                  onChange={(value) => setUnreadOnly(value)}
                />
              </label>
            </Stack>
          </Stack>

          <EmptyNotificationList />
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={{ xs: 2, sm: 0 }}
          >
            {/* 既読・未読 */}
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              justifyContent="start"
            >
              <BaseButton
                variant="outlined"
                size="large"
                sx={{ minWidth: "initial" }}
                onClick={() =>
                  readAllNotifications(
                    notifications
                      .filter((notification) => !notification.isRead)
                      .map((notification) => notification.id),
                  )
                }
              >
                すべて既読にする
              </BaseButton>

              {/* biome-ignore lint/a11y/noLabelWithoutControl: 中身がコントロールなので無視してOK */}
              <label>
                <BaseCheckbox
                  label="未読のみ表示"
                  size="small"
                  value={unreadOnly}
                  onChange={(value) => setUnreadOnly(value)}
                />
              </label>
            </Stack>

            {/* ページ数・表示件数 */}
            <PaginationControls
              page={page}
              perPage={perPage}
              total={totalCount}
            />
          </Stack>

          {/* お知らせ一覧 */}
          <NotificationList
            notifications={notifications}
            offerCargos={Object.values(loadableOfferCargos)
              .filter((loadable) => loadable.status === "success")
              .map((loadable) => loadable.data?.data)}
            readNotification={readNotification}
          />

          {/* ページネーション */}
          <NotificationPagination
            count={count}
            perPage={perPage}
            sx={{ alignSelf: "center" }}
          />
        </Stack>
      )}
    </Stack>
  );
};
